<template>
  <v-card :loading="form.processing" flat>
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-form ref="form" class="mt-4" :disabled="isReadonly || form.processing">
        <v-row align="center">
          <v-col cols="12" md="3">
            <v-switch
              v-model="isSubscription"
              inset
              :label="`For monthly subscription? ${yesOrNoText(
                isSubscription
              )}`"
            ></v-switch>
          </v-col>
          <v-slide-x-transition>
            <v-col v-show="isSubscription" cols="12" md="6">
              <div class="label mb-2">Subscription Order</div>
              <v-autocomplete
                v-model="form.subscription_order_id"
                :items="subscriptionOrders"
                :item-text="subscriptionOrderItemText"
                item-value="id"
                :search-input.sync="searchInputOrder"
                solo
                flat
                outlined
                dense
                placeholder="Choose subscription order"
                cache-items
                @change="changeSubscriptionOrder($event)"
              />
            </v-col>

            <v-col v-if="selectedSubscriptionOrder" cols="12" md="3">
              <v-chip-group column>
                <v-chip
                  v-if="selectedSubscriptionOrder.is_trial"
                  color="warning"
                  dark
                >
                  <strong>1 Month Trial</strong>
                </v-chip>
                <v-chip
                  v-if="selectedSubscriptionOrder.deposit_amount"
                  color="info"
                  dark
                >
                  <strong>
                    Deposit =
                    {{ formatNumber(selectedSubscriptionOrder.deposit_amount) }}
                  </strong>
                </v-chip>
              </v-chip-group>
            </v-col>
          </v-slide-x-transition>
        </v-row>

        <v-row align="center">
          <v-col cols="12" md="3">
            <v-switch
              v-model="isUsePeriod"
              inset
              :label="`Use Term & Month Period? ${yesOrNoText(isUsePeriod)}`"
            ></v-switch>
          </v-col>
          <v-slide-x-transition>
            <v-col v-show="isUsePeriod" cols="12" md="9">
              <v-row align="start">
                <v-col cols="12" md="2">
                  <div class="label mb-2">Term</div>
                  <v-select
                    v-model="form.term"
                    :items="terms"
                    :error="!!form.errors && !!form.errors.term"
                    :error-messages="form.errors.term"
                    solo
                    flat
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <div class="label mb-2">Month</div>
                  <v-select
                    v-model="form.month"
                    :items="months"
                    :error="!!form.errors && !!form.errors.month"
                    :error-messages="form.errors.month"
                    solo
                    flat
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <div class="label mb-2">Period Start Date</div>
                  <v-menu
                    ref="periodStartDateMenu"
                    v-model="periodStartDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.period_start_date"
                        :error="
                          !!form.errors && !!form.errors.period_start_date
                        "
                        :error-messages="form.errors.period_start_date"
                        placeholder="Select start date"
                        prepend-icon="mdi-calendar"
                        solo
                        flat
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.period_start_date"
                      @change="changePeriodStartDate"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="label mb-2">Period End Date</div>
                  <v-menu
                    ref="periodEndDateMenu"
                    v-model="periodEndDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.period_end_date"
                        :error="!!form.errors && !!form.errors.period_end_date"
                        :error-messages="form.errors.period_end_date"
                        placeholder="Select end date"
                        prepend-icon="mdi-calendar"
                        solo
                        flat
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.period_end_date"
                      @change="
                        $refs.periodEndDateMenu.save(form.period_end_date)
                      "
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-col>
          </v-slide-x-transition>
        </v-row>

        <v-row align="start">
          <v-col cols="12" md="4">
            <v-chip
              :color="prop"
              v-if="form.deposit_amount && form.deposit_amount !== 0"
            >
              <strong>
                {{ depositByTerm }}
              </strong>
            </v-chip>
          </v-col>
        </v-row>

        <v-row align="start">
          <v-col cols="12" md="2">
            <div class="label mb-2">Student</div>
            <v-autocomplete
              v-model="form.student_id"
              :items="students"
              :rules="[validationRules.required]"
              :error="!!form.errors && !!form.errors.student_id"
              :error-messages="form.errors.student_id"
              :loading="isStudentsLoading"
              :readonly="isSubscription"
              :background-color="isSubscription ? 'grey lighten-3' : null"
              :search-input.sync="searchInputStudent"
              item-text="name"
              item-value="id"
              solo
              flat
              outlined
              dense
              :placeholder="
                isSubscription ? 'Auto-set by subs order' : 'Choose student'
              "
              cache-items
            />
          </v-col>
          <v-col cols="12" md="2">
            <div class="label mb-2">Teacher</div>
            <v-select
              v-model="form.teacher_id"
              :items="teachers"
              :error="!!form.errors && !!form.errors.teacher_id"
              :error-messages="form.errors.teacher_id"
              :loading="isTeachersLoading"
              :readonly="!form.student_id"
              :background-color="!form.student_id ? 'grey lighten-3' : null"
              item-text="name"
              item-value="id"
              solo
              flat
              outlined
              dense
              placeholder="Choose teacher"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-row align="start">
              <v-col cols="12" md="6">
                <div class="label mb-2">Issue Date</div>
                <v-menu
                  ref="issueDateMenu"
                  v-model="issueDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.issue_date"
                      :rules="[validationRules.required]"
                      :error="!!form.errors && !!form.errors.issue_date"
                      :error-messages="form.errors.issue_date"
                      placeholder="Select issue date"
                      prepend-icon="mdi-calendar"
                      solo
                      flat
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.issue_date"
                    @change="$refs.issueDateMenu.save(form.issue_date)"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" md="6">
                <div class="label mb-2">Due Date</div>
                <v-menu
                  ref="dueDateMenu"
                  v-model="dueDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template #activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.due_date"
                      :rules="[validationRules.required]"
                      :error="!!form.errors && !!form.errors.due_date"
                      :error-messages="form.errors.due_date"
                      placeholder="Select due date"
                      prepend-icon="mdi-calendar"
                      solo
                      flat
                      outlined
                      dense
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="form.due_date"
                    @change="$refs.dueDateMenu.save(form.due_date)"
                  ></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="4">
            <v-row align="start">
              <v-col cols="12" md="6">
                <div class="label mb-2">Status</div>
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  :rules="[validationRules.required]"
                  :error="!!form.errors && !!form.errors.status"
                  :error-messages="form.errors.status"
                  solo
                  flat
                  outlined
                  dense
                  placeholder="Select invoice status"
                  @change="changeInvoiceStatus"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-slide-x-transition hide-on-leave>
                  <div v-if="isShowPaidDate">
                    <div class="label mb-2">Paid Date</div>
                    <v-menu
                      ref="paidDateMenu"
                      v-model="paidDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.paid_date"
                          :rules="[paidDateValidationRules]"
                          :error="!!form.errors && !!form.errors.paid_date"
                          :error-messages="form.errors.paid_date"
                          placeholder="Select paid date"
                          prepend-icon="mdi-calendar"
                          solo
                          flat
                          outlined
                          dense
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.paid_date"
                        @change="$refs.paidDateMenu.save(form.paid_date)"
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </v-slide-x-transition>
                <v-slide-x-transition hide-on-leave>
                  <div v-if="isShowCanceledDate">
                    <div class="label mb-2">Canceled Date</div>
                    <v-menu
                      ref="canceledDateMenu"
                      v-model="canceledDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template #activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.canceled_date"
                          :rules="[canceledDateValidationRules]"
                          :error="!!form.errors && !!form.errors.canceled_date"
                          :error-messages="form.errors.canceled_date"
                          placeholder="Select canceled date"
                          prepend-icon="mdi-calendar"
                          solo
                          flat
                          outlined
                          dense
                          readonly
                          v-bind="attrs"
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="form.canceled_date"
                        @change="
                          $refs.canceledDateMenu.save(form.canceled_date)
                        "
                      ></v-date-picker>
                    </v-menu>
                  </div>
                </v-slide-x-transition>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-if="isShowPaidDate">
          <v-col cols="12" md="3">
            <div class="label mb-2">Payment Ref. No</div>
            <v-text-field
              v-model="form.payment_ref_no"
              solo
              flat
              outlined
              dense
              placeholder="Payment ref. no"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <div class="label mb-2">Proof of Payment URL</div>
            <v-text-field
              v-model="form.proof_of_payment_url"
              solo
              flat
              outlined
              dense
              placeholder="Link to proof of payment"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row align="center">
          <v-col cols="12">
            <div class="label mb-2">Notes</div>
            <v-textarea
              v-model="form.notes"
              solo
              flat
              outlined
              dense
              auto-grow
              placeholder="Additional notes"
            >
            </v-textarea>
          </v-col>
        </v-row>

        <v-card flat class="mb-4">
          <v-card-title class="px-0">
            <v-row>
              <v-col cols="12" md="auto">Invoice Items</v-col>
              <v-col cols="12" md="auto">
                <v-btn
                  depressed
                  color="warning"
                  :block="$vuetify.breakpoint.mobile"
                  @click="addItem"
                >
                  <v-icon left>mdi-plus</v-icon>Add Item
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="px-0">
            <v-list>
              <template v-for="(item, i) in form.items">
                <v-list-item :key="`item-${i}`" :disabled="isReadonly">
                  <v-list-item-content>
                    <v-row>
                      <v-col md="3" cols="12">
                        <div class="label mb-2">Name</div>
                        <v-text-field
                          v-model="item.name"
                          :rules="[validationRules.required]"
                          :error="isItemError(i, 'name')"
                          :error-messages="itemErrorMessage(i, 'name')"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Item name"
                        />
                      </v-col>
                      <v-col md="3" cols="12">
                        <div class="label mb-2">Label</div>
                        <v-select
                          v-model="item.category_id"
                          :items="itemCategories"
                          item-text="label"
                          item-value="id"
                          :loading="isItemCategoriesLoading"
                          :error="isItemError(i, 'category_id')"
                          :error-messages="itemErrorMessage(i, 'category_id')"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Item label"
                        >
                          <template #prepend-item>
                            <v-container>
                              <v-row align="center" dense>
                                <v-col cols="12">
                                  <v-text-field
                                    v-model="newItemCategory.label"
                                    solo
                                    flat
                                    outlined
                                    dense
                                    hide-details
                                    :disabled="isAddItemCategoryLoading"
                                  />
                                </v-col>
                                <v-col cols="12" class="text-right">
                                  <v-btn
                                    depressed
                                    color="primary"
                                    small
                                    :disabled="!newItemCategory.label"
                                    :loading="isAddItemCategoryLoading"
                                    @click="submitNewItemCategory"
                                  >
                                    <v-icon left>mdi-plus</v-icon>
                                    Add new label
                                  </v-btn>
                                </v-col>
                              </v-row>
                            </v-container>
                            <v-divider></v-divider>
                          </template>
                        </v-select>
                      </v-col>
                      <v-col md="1" cols="12">
                        <div class="label mb-2">Qty</div>
                        <v-text-field
                          v-model="item.quantity"
                          :rules="[validationRules.required]"
                          :error="isItemError(i, 'quantity')"
                          :error-messages="itemErrorMessage(i, 'quantity')"
                          type="number"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Quantity"
                          @change="calculateItem(i)"
                        />
                      </v-col>
                      <v-col md="2" cols="12">
                        <div class="label mb-2">Unit Price</div>
                        <v-text-field
                          v-model="item.price"
                          :rules="[validationRules.required]"
                          :error="isItemError(i, 'price')"
                          :error-messages="itemErrorMessage(i, 'price')"
                          prefix="IDR"
                          class="input-right"
                          solo
                          flat
                          outlined
                          dense
                          placeholder="Price per unit"
                          @change="calculateItem(i)"
                        />
                      </v-col>
                      <v-col md="3" cols="12">
                        <v-row>
                          <v-col md="10" cols="12">
                            <div class="label mb-2">Total Price</div>
                            <v-text-field
                              :value="formatNumber(item.total_price)"
                              :rules="[validationRules.required]"
                              prefix="IDR"
                              class="input-right"
                              solo
                              flat
                              outlined
                              dense
                              disabled
                            />
                          </v-col>
                          <v-col md="2" cols="12" align-self="center">
                            <v-btn
                              depressed
                              color="error"
                              icon
                              @click="removeItem(i)"
                            >
                              <v-icon>mdi-delete-forever</v-icon>
                            </v-btn>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
                <v-divider
                  v-if="i < form.items.length - 1"
                  :key="i"
                ></v-divider>
              </template>
            </v-list>
          </v-card-text>
        </v-card>
        <v-card flat>
          <v-card-title class="px-0">Invoice Summary</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <div v-show="isSubscription">
              <div class="label mb-2">Subscription Amount</div>
              <v-text-field
                v-model="subscriptionAmount"
                :rules="[validationRules.required]"
                :error="form.errors.subscription_amount"
                :error-messages="form.errors.subscription_amcunt"
                prefix="IDR"
                suffix="per month"
                class="input-right"
                solo
                flat
                outlined
                dense
                placeholder="Subscription Amount"
              />
            </div>
            <div class="label mb-2">Total Item Price</div>
            <v-text-field
              :value="totalItemPrice"
              prefix="IDR"
              class="input-right"
              solo
              flat
              outlined
              dense
              placeholder="Total Price"
              disabled
            />
            <div class="label mb-2">Deposit Amount</div>
            <v-text-field
              v-model="depositAmount"
              :error="form.errors.deposit_amount"
              :error-messages="form.errors.deposit_amount"
              prefix="IDR"
              class="input-right"
              solo
              flat
              outlined
              dense
              placeholder="Deposit Amount"
            />
            <div class="label mb-2">Total Billed Amount</div>
            <v-text-field
              :value="totalBilledAmount"
              prefix="IDR"
              class="input-right"
              solo
              flat
              outlined
              dense
              placeholder="Total Billed Amount"
              disabled
            />
          </v-card-text>
        </v-card>
        <v-card flat>
          <v-card-title class="px-0">Payment Information</v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="2">
                <v-switch
                  v-model="isUsePaymentMethod"
                  inset
                  hide-details
                  label="Show Payment Info?"
                  @change="changeUsePaymentMethod($event)"
                />
              </v-col>
              <v-col cols="12" md="10">
                <div class="label mb-2">Payment Method</div>
                <v-select
                  v-model="form.payment_method_id"
                  :items="paymentMethods"
                  :item-text="paymentMethodItemText"
                  item-value="id"
                  :loading="isPaymentMethodsLoading"
                  solo
                  flat
                  outlined
                  dense
                  placeholder="Choose payment method"
                  cache-items
                  :disabled="!isUsePaymentMethod"
                  :error="form.errors && form.errors.payment_method_id"
                  :error-messages="form.errors.payment_method_id"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" md="6">
                <v-checkbox v-model="isPaidinAdvance">
                  <template #label>
                    <div>
                      <!-- Main label text: Stays perfectly aligned on the same line as the checkbox -->
                      <div class="text-body-1 text-high-emphasis">
                        Paid in Advance?
                      </div>

                      <!-- Disclaimer text: Breaks to a new line, smaller font, and lighter color -->
                      <div class="text-caption text-medium-emphasis mt-1">
                        *Generating Invoices for 2nd Month and 3rd Month with a
                        IDR 0 Billed Amount
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-container fluid>
        <v-row justify="end">
          <v-col md="3" cols="12">
            <v-btn block depressed outlined @click="cancel">Cancel</v-btn>
          </v-col>
          <v-col md="3" cols="12">
            <v-btn
              block
              depressed
              color="primary"
              :disabled="isReadonly || form.processing"
              @click="submit"
            >
              Save Changes
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-card-actions>
  </v-card>
</template>
<script>
import constant from '@/constant'
import { mapActions, mapGetters } from 'vuex'
export default {
  name: 'InvoiceForm',
  props: {
    mode: {
      type: String,
      required: true,
      validator: () => ['add', 'edit', 'view'],
    },
    invoice: {
      type: Object,
      default: null,
      required: false,
    },
    subscriptionOrderId: {
      type: Number,
      default: null,
      required: false,
    },
    issueDate: {
      type: String,
      default: null,
      required: false,
    },
    dueDate: {
      type: String,
      default: null,
      required: false,
    },
    term: {
      type: Number,
      default: null,
      required: false,
    },
    month: {
      type: Number,
      default: null,
      required: false,
    },
  },
  data() {
    return {
      issueDateMenu: false,
      dueDateMenu: false,
      paidDateMenu: false,
      canceledDateMenu: false,
      periodStartDateMenu: false,
      periodEndDateMenu: false,
      isSubscription: true,
      isUsePaymentMethod: false,
      isPaidinAdvance: false,
      depositByTerm: '',
      isUsePeriod: true,
      form: this.$inertia.form({
        subscription_order_id: null,
        student_id: null,
        teacher_id: null,
        status: null,
        issue_date: null,
        due_date: null,
        notes: null,
        subscription_amount: 0,
        deposit_amount: 0,
        total_billed_amount: 0,
        items: [],
        term: null,
        month: null,
        payment_method_id: null,
        paid_date: null,
        canceled_date: null,
        period_start_date: null,
        period_end_date: null,
        payment_ref_no: null,
        proof_of_payment_url: null,
      }),
      newItemCategory: {
        label: null,
      },
      searchInputOrder: null,
      searchInputStudent: null,
      searchInputTeacher: null,
    }
  },
  computed: {
    ...mapGetters({
      students: 'invoice/students',
      isStudentsLoading: 'invoice/isStudentsLoading',
      subscriptionOrders: 'invoice/subscriptionOrders',
      isSubscriptionOrdersLoading: 'invoice/isSubscriptionOrdersLoading',
      teachers: 'invoice/teachers',
      isTeachersLoading: 'invoice/isTeachersLoading',
      paymentMethods: 'invoice/paymentMethods',
      isPaymentMethodsLoading: 'invoice/isPaymentMethodsLoading',
      itemCategories: 'invoice/itemCategories',
      deposit: 'invoice/deposit',
      isItemCategoriesLoading: 'invoice/isItemCategoriesLoading',
      isAddItemCategoryLoading: 'invoice/isAddItemCategoryLoading',
    }),
    isReadonly() {
      return this.mode === 'view'
    },
    statusOptions() {
      return constant.invoiceStatusOptions
    },
    isShowPaidDate() {
      return ['PAID'].includes(this.form.status)
    },
    isShowCanceledDate() {
      return ['CANCELED'].includes(this.form.status)
    },
    selectedSubscriptionOrder() {
      return this.form.subscription_order_id
        ? this.subscriptionOrders.find(
            (e) => e.id === this.form.subscription_order_id
          )
        : null
    },
    subscriptionAmount: {
      get() {
        return this.formatNumber(this.form.subscription_amount)
      },
      set(value) {
        this.form.subscription_amount = this.parseNumber(value)
      },
    },
    depositAmount: {
      get() {
        return this.formatNumber(this.form.deposit_amount)
      },
      set(value) {
        this.form.deposit_amount = this.parseNumber(value)
      },
    },
    totalBilledAmount() {
      const totalBilledAmount =
        this.totalItemPrice +
        this.form.subscription_amount -
        this.form.deposit_amount
      return this.formatNumber(totalBilledAmount)
    },
    totalItemPrice() {
      return this.form.items
        .map((e) => e.total_price)
        .reduce((prev, curr) => prev + curr, 0)
    },
    paymentMethodValidationRules() {
      if (this.isUsePaymentMethod) {
        return (v) => !!v || 'Payment method is required'
      }
      return () => true
    },
    paidDateValidationRules() {
      if (this.form.status === 'PAID') {
        return (v) => !!v || 'Paid date is required'
      }
      return () => true
    },
    canceledDateValidationRules() {
      if (this.form.status === 'CANCELED') {
        return (v) => !!v || 'Canceled date is required'
      }
      return () => true
    },
    paidInAdvanceValidationRules() {
      if (this.form.status !== 'PAID' && this.form.month !== 1) {
        return (v) =>
          !!v ||
          'When this is checked, the required status is PAID, and the required month is 1.'
      }
      return () => true
    },
  },
  watch: {
    'form.term': function () {
      this.checkAndHandleTermMonth()
    },
    'form.month': function () {
      this.checkAndHandleTermMonth()
    },
    $props: {
      immediate: true,
      handler() {
        this.validateProps()
      },
    },
    searchInputStudent(val) {
      if (val) {
        this.debouncedSearchStudents()
      }
    },
    searchInputOrder(newVal, oldVal) {
      if (newVal) {
        const newParts = newVal.split('-').map((e) => e.trim())
        const oldParts = oldVal ? oldVal.split('-').map((e) => e.trim()) : ['']
        if (newParts[0] != oldParts[0]) {
          this.debouncedSearchSubscriptionOrders()
        }
      }
    },
    'form.student_id': function (newVal, oldVal) {
      if (newVal && newVal != oldVal) {
        this.searchTeachers()
      }
    },
  },
  created() {
    this.init()
    this.debouncedSearchSubscriptionOrders = _.debounce(
      this.searchSubscriptionOrders,
      500
    )
    this.debouncedSearchStudents = _.debounce(this.searchStudents, 500)
  },
  methods: {
    ...mapActions({
      getStudents: 'invoice/getStudents',
      getSubscriptionOrders: 'invoice/getSubscriptionOrders',
      getDepositBySubscriptionOrderAndTerm:
        'invoice/getDepositBySubscriptionOrderAndTerm',
      getTeachers: 'invoice/getTeachers',
      getPaymentMethods: 'invoice/getPaymentMethods',
      getItemCategories: 'invoice/getItemCategories',
      addItemCategory: 'invoice/addItemCategory',
    }),
    validateProps() {
      if ((this.mode === 'edit' || this.mode === 'view') && !this.invoice) {
        console.error(
          `invoice props must be provided for 'edit' and 'view' mode`
        )
      }
    },
    async init() {
      if (this.mode != 'add' && this.invoice != null) {
        _.merge(this.form, this.invoice)
        this.isSubscription = this.invoice.is_subscription
        this.isUsePaymentMethod = !!this.invoice.payment_method_id
        this.isUsePeriod = !!this.invoice.term || !!this.invoice.month
        await Promise.all([
          this.getSubscriptionOrders({
            filters: {
              id: this.invoice.subscription_order_id,
            },
          }),
          this.getStudents({
            filters: {
              id: this.invoice.student_id,
            },
          }),
        ])
      } else {
        // if (this.subscriptionOrderId) {
        await this.getSubscriptionOrders({
          filters: {
            id: this.subscriptionOrderId,
          },
        })
        this.form.subscription_order_id = this.subscriptionOrderId
        this.isSubscripption = this.form.subscription_order_id != null
        this.changeSubscriptionOrder(this.subscriptionOrderId)
        // }
        this.form.issue_date = this.issueDate
        this.form.due_date = this.dueDate
        this.form.term = this.term
        this.form.month = this.month
      }
      await Promise.all([this.getPaymentMethods(), this.getItemCategories()])
    },
    async checkAndHandleTermMonth() {
      if (this.form.term && this.form.month && this.form.month == 3) {
        await this.getDepositBySubscriptionOrderAndTerm({
          filters: {
            id: this.form.subscription_order_id,
            term: this.form.term,
          },
        })
        this.form.deposit_amount = this.deposit.price
        this.depositByTerm = `There is a deposit in Term ${
          this.form.term
        }: IDR ${this.formatNumber(this.deposit.price)}`
      } else {
        this.form.deposit_amount = 0
      }
    },
    async searchSubscriptionOrders() {
      await this.getSubscriptionOrders({
        search: this.searchInputOrder,
        filters: {
          status: ['INACTIVE', 'ACTIVE'],
        },
      })
    },
    async searchStudents() {
      await this.getStudents({
        search: this.searchInputStudent,
        filters: {
          status: 'ACTIVE',
        },
      })
    },
    subscriptionOrderItemText(item) {
      return item ? `${item.code} - ${item.student?.name ?? '-'}` : ''
    },
    async changeSubscriptionOrder(value) {
      const subscriptionOrder = this.subscriptionOrders.find(
        (e) => e.id === value
      )
      if (subscriptionOrder) {
        await this.getStudents({
          filters: {
            id: subscriptionOrder.student_id,
          },
        })
        this.form.student_id = subscriptionOrder.student_id
        this.form.subscription_amount = subscriptionOrder.monthly_fee
        this.form.total_billed_amount =
          this.form.subscription_amount - this.form.deposit_amount
      }
    },
    async searchTeachers() {
      await this.getTeachers({
        filters: {
          private_student_id: [this.form.student_id],
        },
      })
    },
    async submitNewItemCategory() {
      const request = { ...this.newItemCategory }
      await this.addItemCategory(request)
      this.newItemCategory.label = null
    },
    addItem() {
      this.form.items.push({
        name: null,
        notes: null,
        price: 0,
        quantity: 0,
        total_price: 0,
        category_id: null,
      })
    },
    removeItem(index) {
      this.form.items.splice(index, 1)
    },
    isItemError(index, prop) {
      const errorProp = `items.${index}.${prop}`
      return this.form.errors.items && this.form.errors.items[errorProp]
    },
    itemErrorMessage(index, prop) {
      const errorProp = `items.${index}.${prop}`
      return this.form.errors.items ? this.form.errors.items[errorProp] : null
    },
    calculateItem(index) {
      const item = this.form.items[index]
      if (item) {
        item.total_price = item.price * item.quantity
      }
    },
    changeUsePaymentMethod(value) {
      if (!value) {
        this.form.payment_method_id = null
      }
    },
    paymentMethodItemText(item) {
      return `${item.name} (${item.bank_code}: ${item.account_number} a/n ${item.account_name})`
    },
    changePeriodStartDate() {
      this.form.period_end_date = moment(this.form.period_start_date)
        .add(4, 'months')
        .add(2, 'weeks')
        .format('YYYY-MM-DD')
      this.$refs.periodStartDateMenu.save(this.form.period_start_date)
    },
    changeInvoiceStatus(status) {
      if (status === 'PAID') {
        this.form.paid_date = moment().format('YYYY-MM-DD')
        this.form.canceled_date = null
      } else if (status === 'CANCELED') {
        this.form.canceled_date = moment().format('YYYY-MM-DD')
        this.form.paid_date = null
      } else {
        this.form.paid_date = null
        this.form.canceled_date = null
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    async submit() {
      console.log(this.$refs.form)
      const validationResult = this.$refs.form.validate()
      console.log(`validationResult: ${validationResult}`)
      if (this.$refs.form.validate()) {
        if (this.mode === 'add') {
          if (!this.isPaidinAdvance) {
            this.form.post('/invoices')
          } else {
            this.form.post('/invoices/create-bulk')
          }
        } else if (this.mode === 'edit') {
          this.form.put(`/invoices/${this.invoice.id}`)
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.input-right {
  :deep(input) {
    text-align: right;
  }
}
</style>
